/* =========================================================================
   ClueVine — dictionary service
   Wraps the bundled word list behind a small, stable interface. Built once at
   module load: deduped, length-bucketed, and indexed by anagram key. A
   defensive /^[a-z]+$/ filter guarantees only clean lowercase words enter the
   game, so malformed source data can never reach a puzzle.

   Swap the corpus by changing what's passed to createDictionary() below; no
   game logic depends on the source.
   ========================================================================= */

import { ANSWER_WORDS, VALID_WORDS } from '../data/wordlists';
import type { Dictionary, WordsFromLettersOptions } from './types';

const CLEAN = /^[a-z]+$/;

function countLetters(letters: Iterable<string>): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const ch of letters) counts[ch] = (counts[ch] ?? 0) + 1;
  return counts;
}

/**
 * Build a dictionary. `source` is the valid-guess corpus; `answerSource`
 * (defaults to `source`) is the answer-quality pool used for picking secrets/
 * targets/seeds. Answer words are filtered to those present in the valid corpus,
 * so every answer is always an acceptable guess.
 */
export function createDictionary(
  source: readonly string[],
  answerSource: readonly string[] = source,
): Dictionary {
  // Normalize → filter → dedupe → sort.
  const all: string[] = [];
  const seen = new Set<string>();
  for (const raw of source) {
    const w = raw.toLowerCase();
    if (!CLEAN.test(w) || seen.has(w)) continue;
    seen.add(w);
    all.push(w);
  }
  all.sort();

  const sameSource = answerSource === source;
  const answerByLen = new Map<number, string[]>();
  if (!sameSource) {
    const ansSeen = new Set<string>();
    for (const raw of answerSource) {
      const w = raw.toLowerCase();
      if (!CLEAN.test(w) || ansSeen.has(w) || !seen.has(w)) continue;
      ansSeen.add(w);
      const bucket = answerByLen.get(w.length);
      if (bucket) bucket.push(w);
      else answerByLen.set(w.length, [w]);
    }
  }

  const byLen = new Map<number, string[]>();
  const anagramIndex = new Map<string, string[]>();

  const keyOf = (word: string) => word.split('').sort().join('');

  const EMPTY: readonly string[] = [];
  // Index of the first element >= target in a sorted array (binary search).
  const lowerBound = (arr: readonly string[], target: string): number => {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (arr[mid] < target) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  for (const w of all) {
    const bucket = byLen.get(w.length);
    if (bucket) bucket.push(w);
    else byLen.set(w.length, [w]);

    const k = keyOf(w);
    const group = anagramIndex.get(k);
    if (group) group.push(w);
    else anagramIndex.set(k, [w]);
  }

  const dict: Dictionary = {
    size: all.length,

    isValid(word) {
      return seen.has(word.toLowerCase());
    },

    answersByLength(length) {
      return (sameSource ? byLen : answerByLen).get(length) ?? [];
    },

    byLength(length) {
      return byLen.get(length) ?? [];
    },

    anagramKey(word) {
      return keyOf(word.toLowerCase());
    },

    anagramsOf(letters) {
      return anagramIndex.get(keyOf(letters.toLowerCase())) ?? [];
    },

    wordsFromLetters(letters: readonly string[], options: WordsFromLettersOptions = {}) {
      const { required, minLength = 4, maxLength = 15, allowRepeats = true } = options;
      const lower = letters.map((l) => l.toLowerCase());
      const set = new Set(lower);
      const avail = countLetters(lower);
      const req = required?.toLowerCase();
      const out: string[] = [];

      for (let len = minLength; len <= maxLength; len++) {
        const bucket = byLen.get(len);
        if (!bucket) continue;
        for (const word of bucket) {
          if (req && !word.includes(req)) continue;
          if (allowRepeats) {
            let ok = true;
            for (const ch of word) {
              if (!set.has(ch)) {
                ok = false;
                break;
              }
            }
            if (ok) out.push(word);
          } else {
            const need = countLetters(word);
            let ok = true;
            for (const ch in need) {
              if ((avail[ch] ?? 0) < need[ch]) {
                ok = false;
                break;
              }
            }
            if (ok) out.push(word);
          }
        }
      }
      return out;
    },

    hasPrefix(prefix, length) {
      const p = prefix.toLowerCase();
      const arr = length != null ? (byLen.get(length) ?? EMPTY) : all;
      if (p === '') return arr.length > 0;
      const i = lowerBound(arr, p);
      return i < arr.length && arr[i].startsWith(p);
    },
  };

  return dict;
}

/** The default dictionary: 32k valid-guess corpus + curated answer pool. */
export const dictionary: Dictionary = createDictionary(VALID_WORDS, ANSWER_WORDS);
